/**
 * ES Cross-Sell Engine v4 — Essential Stencil
 * Hybrid: Shopify product recommendations + always-include-tools rule
 *
 * How it works:
 *   1. Fetches Shopify recs based on first cart item (real purchase data)
 *   2. Filters out items already in cart
 *   3. If no tool/brush in recs, fetches one from brushes collection
 *   4. Returns ranked results with tool guaranteed in the mix
 *
 * Upload to: Assets/es-crosssell-engine.js
 * Load in theme.liquid AFTER es-cart-settings.liquid
 *
 * API:
 *   ESCrossSell.forCart({ limit })                 → Promise<Product[]>
 *   ESCrossSell.forPDP(productObj, { limit })      → Promise<Product[]>
 *   ESCrossSell.forItem(cartItem, { limit })       → Promise<Product[]>
 *   ESCrossSell.detectType(src)                    → 'stencil'|'transfer'|'tool'
 *   ESCrossSell.nextTier(cartCents)                → { cents, label } | null
 *   ESCrossSell.tierGap(cartCents)                 → number (cents)
 *   ESCrossSell.invalidate()                       → void (clear cache)
 */
(function () {
  'use strict';

  var _cart = null;
  var _cartAt = 0;
  var _toolCache = null;
  var TTL = 30000;

  function fmt(cents) { return '$' + (cents / 100).toFixed(2); }

  /* ════════════════════════════════════════
     TYPE DETECTION
  ════════════════════════════════════════ */
  function detectType(src) {
    if (!src) return 'stencil';
    if (typeof src === 'object' && !Array.isArray(src)) {
      return detectType(src.product_type || src.tags || []);
    }
    if (Array.isArray(src)) {
      for (var i = 0; i < src.length; i++) {
        var tl = src[i].toLowerCase();
        if (tl === 'rub on transfer' || tl === 'rub-on transfer') return 'transfer';
        if (tl === 'brushes' || tl === 'art and craft supply') return 'tool';
      }
      return 'stencil';
    }
    var s = String(src).toLowerCase();
    if (s.indexOf('transfer') !== -1) return 'transfer';
    if (s === 'brushes' || s === 'art and craft supply') return 'tool';
    return 'stencil';
  }

  /* ════════════════════════════════════════
     TIER HELPERS
  ════════════════════════════════════════ */
  function getTiers() {
    var c = window.CDCSettings || {};
    var t = [];
    var s1 = c.shippingThreshold || c.tier1Threshold;
    if (s1) t.push({ cents: Math.round(s1 * 100), label: c.tier1Message || 'Free Shipping' });
    if (c.enableTiers) {
      if (c.tier2Threshold) t.push({ cents: Math.round(c.tier2Threshold * 100), label: c.tier2Message || 'Free Gift' });
      if (c.tier3Threshold) t.push({ cents: Math.round(c.tier3Threshold * 100), label: c.tier3Message || 'Free Gift' });
    }
    t.sort(function (a, b) { return a.cents - b.cents; });
    return t;
  }
  function nextTier(cents) {
    var t = getTiers();
    for (var i = 0; i < t.length; i++) { if (cents < t[i].cents) return t[i]; }
    return null;
  }
  function tierGap(cents) {
    var t = nextTier(cents);
    return t ? Math.max(0, t.cents - cents) : 0;
  }

  /* ════════════════════════════════════════
     NORMALIZE
  ════════════════════════════════════════ */
  function toCents(v) {
    if (!v) return 0;
    var s = String(v);
    return s.indexOf('.') !== -1 ? Math.round(parseFloat(s) * 100) : parseInt(s, 10);
  }

  function normalize(p) {
    var v = (p.variants && p.variants[0]) || {};
    var pc = toCents(v.price);
    var cap = toCents(v.compare_at_price);
    var img = '';
    if (p.featured_image) {
      img = typeof p.featured_image === 'string' ? p.featured_image : (p.featured_image.src || '');
    } else if (p.images && p.images.length) {
      img = typeof p.images[0] === 'string' ? p.images[0] : (p.images[0].src || '');
    }

    return {
      id:         p.id,
      variantId:  v.id,
      handle:     p.handle || '',
      title:      p.title || '',
      type:       detectType(p),
      price:      pc,
      compareAt:  cap > pc ? cap : 0,
      image:      img ? img.replace(/(\.[^.]+)(\?|$)/, '_240x240$1$2') : '',
      available:  v.available !== false,
      url:        p.url || ('/products/' + (p.handle || ''))
    };
  }

  /* ════════════════════════════════════════
     FETCH HELPERS
  ════════════════════════════════════════ */
  function fetchCart() {
    if (_cart && (Date.now() - _cartAt) < TTL) return Promise.resolve(_cart);
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) { _cart = c; _cartAt = Date.now(); return c; })
      .catch(function () { return { items: [], total_price: 0, item_count: 0 }; });
  }

  function fetchRecs(productId, limit) {
    return fetch('/recommendations/products.json?product_id=' + productId + '&limit=' + (limit || 10), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (d) { return (d.products || []).map(normalize).filter(function (p) { return p.available; }); })
      .catch(function () { return []; });
  }

  function fetchBestTool() {
    if (_toolCache) return Promise.resolve(_toolCache);
    return fetch('/collections/brushes/products.json?limit=5', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var tools = (d.products || []).map(normalize).filter(function (p) { return p.available; });
        _toolCache = tools;
        return tools;
      })
      .catch(function () { return []; });
  }

  /* ════════════════════════════════════════
     CORE: recommend()
     1. Get Shopify recs for the anchor product
     2. Filter out cart items
     3. Ensure a tool is in the results
     4. Return up to limit
  ════════════════════════════════════════ */
  function recommend(opts) {
    var anchorId   = opts.anchorProductId;
    var exclude    = opts.excludeIds || [];
    var limit      = opts.limit || 3;

    if (!anchorId) return Promise.resolve([]);

    return fetchRecs(anchorId, 10).then(function (recs) {
      var filtered = recs.filter(function (p) { return exclude.indexOf(p.id) === -1; });

      var hasTool = filtered.some(function (p) { return p.type === 'tool'; });

      if (hasTool) {
        return filtered.slice(0, limit);
      }

      /* No tool in Shopify recs — fetch and inject one */
      return fetchBestTool().then(function (tools) {
        var availableTools = tools.filter(function (t) { return exclude.indexOf(t.id) === -1; });
        /* Take top results, replace last slot with best tool */
        var results = filtered.slice(0, limit);
        if (availableTools.length && results.length >= limit) {
          results[limit - 1] = availableTools[0];
        } else if (availableTools.length) {
          results.push(availableTools[0]);
        }
        return results.slice(0, limit);
      });
    });
  }

  /* ════════════════════════════════════════
     CONVENIENCE METHODS
  ════════════════════════════════════════ */
function forCart(opts) {
    opts = opts || {};
    return fetchCart().then(function (cart) {
      if (!cart.items || !cart.items.length) return [];
      var excludeIds = cart.items.map(function (i) { return i.product_id; });
      var limit = opts.limit || 3;

      /* Fetch recs from up to 3 cart items as anchors */
      var anchors = cart.items.slice(0, 3);
      var recPromises = anchors.map(function (item) {
        return fetchRecs(item.product_id, 10);
      });

      return Promise.all(recPromises).then(function (allRecs) {
        var seen = {};
        var pool = [];
        allRecs.forEach(function (recs) {
          recs.forEach(function (p) {
            if (!seen[p.id] && excludeIds.indexOf(p.id) === -1) {
              seen[p.id] = true;
              pool.push(p);
            }
          });
        });

        var hasTool = pool.some(function (p) { return p.type === 'tool'; });

        if (hasTool) {
          return pool.slice(0, limit);
        }

        return fetchBestTool().then(function (tools) {
          var availableTools = tools.filter(function (t) { return excludeIds.indexOf(t.id) === -1; });
          var results = pool.slice(0, limit);
          if (availableTools.length && results.length >= limit) {
            results[limit - 1] = availableTools[0];
          } else if (availableTools.length) {
            results.push(availableTools[0]);
          }
          return results.slice(0, limit);
        });
      });
    });
  }

  function forPDP(product, opts) {
    opts = opts || {};
    return fetchCart().then(function (cart) {
      var excludeIds = [product.id].concat(cart.items.map(function (i) { return i.product_id; }));
      return recommend({
        anchorProductId: product.id,
        excludeIds:      excludeIds,
        limit:           opts.limit || 2
      });
    });
  }

  function forItem(cartItem, opts) {
    opts = opts || {};
    return fetchCart().then(function (cart) {
      var excludeIds = cart.items.map(function (i) { return i.product_id; });
      return recommend({
        anchorProductId: cartItem.product_id,
        excludeIds:      excludeIds,
        limit:           opts.limit || 1
      });
    });
  }

  /* ════════════════════════════════════════
     CACHE INVALIDATION
  ════════════════════════════════════════ */
  function invalidate() { _cart = null; _cartAt = 0; }

  document.addEventListener('cdc:cart-updated', invalidate);
  document.addEventListener('cdc:item-added', invalidate);

  /* ════════════════════════════════════════
     PUBLIC API
  ════════════════════════════════════════ */
  window.ESCrossSell = {
    forCart:      forCart,
    forPDP:       forPDP,
    forItem:      forItem,
    recommend:    recommend,
    detectType:   detectType,
    getTiers:     getTiers,
    nextTier:     nextTier,
    tierGap:      tierGap,
    normalize:    normalize,
    invalidate:   invalidate
  };

})();